import { Fragment, useCallback, useState } from "react";
import { Link } from "react-router-dom";

import type {
  Family,
  FamilyLink,
  FamilyRevRows,
  FamilySignal,
  FamilySummary,
} from "../api/types";
import { Empty, FoldablePanel, Note } from "./Panel";
import { PnLShape } from "./PnLShape";
import { useJournalShapes } from "../hooks/useShapes";
import {
  fragility,
  fragilityTone,
  money,
  moneyTone,
  rateTone,
  rateWord,
  shapeOf,
} from "../lib/shape";
import { useFamilyPool, useFamilySignals, useFamilySummary } from "../hooks/useFamily";
import { shortWallet } from "../lib/signals";
import {
  type EdgeCounts,
  agreedGroup,
  agreedLabel,
  countsFromSummary,
  expectedWord,
  hitWord,
  lead,
  linkSourceWord,
  linksWithin,
  marketWord,
  outcomeWord,
  pct,
  physEdge,
  physZ,
  poolTone,
  poolWord,
  priceEdge,
  priceZ,
  residZ,
  revCutWord,
  revMixWord,
  rowTone,
  share,
  splitByAgreement,
  usd,
  verdict,
  walletsWord,
  zTone,
  zWord,
} from "../lib/family";
import { StrategyLiveControls } from "./StrategyLiveControls";
import { StrategyRowActions } from "./StrategyRowActions";

/**
 * Консенсус семьи кошельков — замена копирования по доходности.
 *
 * # Почему пришлось заменить, а не докрутить
 *
 * «Копирование ставок» (см. `FollowLegacy` ниже) отбирало кошелёк по ПРИБЫЛИ —
 * и провалилось измеримо: шесть сценариев, 1730 сведённых сигналов, кромки нет
 * ни в одном (перенос ROI с недели на неделю r = 0.11 — отбор каждую неделю
 * даёт новый список случайных счастливчиков). Причина в том, ЧТО отбирали и
 * ПО ЧЕМУ: одиночный кошелёк слишком шумный сам по себе, а прибыль — метрика с
 * нулевой памятью периода.
 *
 * Здесь меняются оба конца. Копируется не кошелёк, а СЕМЬЯ — группа адресов
 * одного оператора, найденная по совпадению СЕКУНДЫ входа (у обычной пары доля
 * общих окон с совпадением ≤ 2 с равна 0.005 при нуле 0.012, у связанной —
 * медиана 0.50 при нуле 0.04), — и отбирается семья ОСТАТКОМ НАД ФИЗИКОЙ
 * (перенос r = 0.62, вшестеро надёжнее прибыли), а не итогом.
 *
 * Вне выборки (пул зафиксирован на первой половине недели, суд — на второй)
 * консенсус ОДНОЙ семьи дал 178 срабатываний за 3.2 суток при точности
 * 0.742 ± 0.033 против цены входа 0.699 и физики 0.691 — кромку +4.3 п.п. над
 * ценой и +5.1 п.п. над физикой. См. `lib/family.ts` про то, как считается z
 * без хранимой в базе Σp(1−p) — этой веха не трогает storage/family.go.
 *
 * # Чем ограничена трата денег
 *
 * Кнопок на панели не было вовсе, и довод был сильный: у подсистемы не было
 * порта постановки ордера, а кнопка по строке, за которой не может стоять
 * ордер, обещала бы действие, которого не существует. С ADR-0022 порт есть
 * (`trader/family/live.go`), и кнопки появились ровно там, где за ними стоит
 * ордер: переключатель режима над таблицей (StrategyLiveControls) и
 * «взять / пропустить» по строке ленты (StrategyRowActions).
 *
 * Обещание при этом не ослабло, а сменило природу. Нажатие превращается в ордер
 * только при совпадении ТРЁХ условий: порт передан трейдеру в сборке, потолок
 * окружения (FAMILY_MODE, FAMILY_ROLE) разрешает торговать и ключ оператора
 * разрешает торговать. Действующий режим — строгий МИНИМУМ двух последних,
 * поэтому кнопка умеет только ПОНИЗИТЬ; поднять потолок умеет лишь правка .env.
 * Сколько это стоит, ограничивают FAMILY_DAILY_CAP_USD и
 * FAMILY_MAX_OPEN_ORDERS. Клетки, как у почти решённого, здесь нет — по окну и
 * стороне пишется одна строка, — а заявку снимают разворот семьи и свипер,
 * ходящий по БАЗЕ: срока жизни у мейкерской заявки нет, риск-гейт отвергает GTD
 * на всех сроках, какие бывают у пятиминутного окна.
 */
export function FamilyPanel({ hours, windowLen }: { hours: number; windowLen: number }) {
  // Срез по ревизии состава — состояние ПАНЕЛИ, а не сервера: умолчание «все»
  // сохраняет прежний ответ, а выбор среза остаётся решением оператора.
  // Считать срез в браузере нельзя: резать пришлось бы уже сложенную сумму, а
  // сложены в ней разные отборы.
  const [rev, setRev] = useState(0);
  const summary = useFamilySummary(hours, rev, windowLen);
  // Разрез по числу сошедшихся семей (одна против двух и больше) сервер не
  // умеет отдать: `/family/summary` режет только по семье, а по агрегату
  // `families_agreed` — нет отдельной SQL-агрегации (storage/family.go эта
  // веха не трогает). Считаем разрез САМИ по сырой ленте, максимальным лимитом
  // — и честно показываем, если лента этот лимит упёрла (см. AgreementNote).
  const feed = useFamilySignals(hours, 500, rev, windowLen);
  // Форма счёта — общим запросом на все журналы: границы корзин обязаны быть
  // одни, иначе гистограммы соседних панелей перестанут сравниваться.
  const shapes = useJournalShapes(hours, "family", windowLen);

  const revisions = summary.data?.revisions ?? [];
  const families = summary.data?.families ?? {};
  const familyEntries = Object.entries(families).sort(([a], [b]) => a.localeCompare(b));
  const overall = summary.data ? countsFromSummary(summary.data.summary) : null;

  return (
    <section>
      <h3 className="mb-2 text-xs font-medium text-[var(--color-muted)]">
        консенсус семьи{overall ? ` — ${verdict(overall)}` : ""}
      </h3>
      {!summary.data ? (
        <Empty>{summary.isError ? "сводка не загрузилась" : "загрузка…"}</Empty>
      ) : overall === null || overall.signals === 0 ? (
        <Empty>
          срабатываний ещё не было: сигнал рождается, когда двое и больше
          кошельков ОДНОЙ семьи пула сходятся на одной стороне окна
        </Empty>
      ) : (
        <>
          {/* Управление живым режимом — над таблицей, по тому же доводу, что у
              почти решённого: решение принимается по числам замера. Клетки у
              семьи нет — по окну и стороне пишется одна строка. */}
          <StrategyLiveControls id="family" />
          <RevCut rev={rev} revisions={revisions} onPick={setRev} />
          {/* ДЕНЬГИ — собранными и над таблицей: раздел спрашивают «сколько
              принесло за окно», и ответ обязан быть готовым числом, а не
              собираться глазом по строкам семей. Знаменатели у двух теней
              разные, и оба названы: мейкер считается по налитым, тейкер — по
              сведённым. */}
          <p className="mt-3 text-xs text-[var(--color-muted)]">
            за окно: мейкером{" "}
            <b className="num" style={{ color: moneyTone(summary.data.summary.shadow_maker_pnl) }}>
              {money(summary.data.summary.shadow_maker_pnl)}
            </b>{" "}
            по {summary.data.summary.maker_filled} налитым · тейкером{" "}
            <b className="num" style={{ color: moneyTone(summary.data.summary.shadow_taker_pnl) }}>
              {money(summary.data.summary.shadow_taker_pnl)}
            </b>{" "}
            по {summary.data.summary.settled} сведённым
          </p>
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-left text-[var(--color-muted)]">
                <tr>
                  <th className="pb-2 font-normal">семья</th>
                  <th className="pb-2 text-right font-normal">зачётных</th>
                  <th className="pb-2 text-right font-normal">сведено</th>
                  <th className="pb-2 text-right font-normal">угадано</th>
                  <th className="pb-2 text-right font-normal">против цены</th>
                  <th className="pb-2 text-right font-normal">против физики</th>
                  <th
                    className="pb-2 text-right font-normal"
                    title="мейкерская тень за окно сводок, по налитым строкам"
                  >
                    мейкер
                  </th>
                  <th
                    className="pb-2 text-right font-normal"
                    title="тейкерская тень за окно сводок, по сведённым строкам"
                  >
                    тейкер
                  </th>
                  <th className="pb-2 text-right font-normal">форма счёта</th>
                </tr>
              </thead>
              <tbody>
                {familyEntries.map(([id, s]) => (
                  <FamilySummaryRow
                    key={id}
                    familyID={id}
                    counts={countsFromSummary(s)}
                    raw={s}
                    shape={shapeOf(shapes, id)}
                  />
                ))}
              </tbody>
            </table>
          </div>
          {/* УДЕЛЬНЫЙ СЧЁТ — строкой на семью, и ТОЛЬКО здесь. Сравнение с
              соседями ставка по-прежнему несёт: подсистема заведена позже всех,
              и её сумма по построению меньше их сумм. Но в таблице ей не место —
              сроки строк-семей расходятся в десятки раз, и колонка ставок
              складывалась глазом в общий минус портфеля. Читать ставку можно
              только вместе со сроком и плотностью наблюдения, которые едут
              здесь же — по тому же правилу, по которому рядом с суммой едет её
              знаменатель. */}
          <Note label="ставка и хрупкость — по семьям">
            <ul className="space-y-1 text-xs text-[var(--color-muted)]">
              {familyEntries.map(([id]) => {
                const shape = shapeOf(shapes, id);
                return (
                  <li key={`rate|${id}`}>
                    <b>{id}</b> —{" "}
                    <b style={{ color: rateTone(shape, hours) }}>{rateWord(shape, hours)}</b>;{" "}
                    <span style={{ color: fragilityTone(shape) }}>{fragility(shape)}</span>.
                  </li>
                );
              })}
            </ul>
          </Note>
          {feed.data && <AgreementNote signals={feed.data.signals} overall={overall} />}
          <Note label="как читать: z-оценки, знаменатели и что даёт кнопка">
            <b>«Против цены» и «против физики» — это z-оценки</b>, а не разности:
            кромка объявляется запасом в стандартных ошибках, потому что знак
            разности на такой выборке не значит ничего. Зелёное — от двух сигм и
            не раньше. <b>Знаменатели разные и оба видны:</b> строка без стакана
            не входит в сумму цены, строка без замка — в сумму физики, и кромка
            над ценой без кромки над физикой означает, что куплена сторона,
            которая и так выигрывала. <b>Ордер по строке возможен, но не сам
            собой:</b> кнопки выше и в ленте ничего не включают, пока торговать не
            разрешат ОБА сразу — потолок окружения и ключ оператора; на окно и
            сторону приходится одна строка и не больше одного ордера, а суточный
            расход и число открытых заявок ограничены потолками стратегии.
            Разворот семьи нашу заявку СНИМАЕТ.
          </Note>
        </>
      )}
    </section>
  );
}

function FamilySummaryRow({
  familyID,
  counts,
  raw,
  shape,
}: {
  familyID: string;
  counts: EdgeCounts;
  raw: FamilySummary;
  shape: ReturnType<typeof shapeOf>;
}) {
  const pz = priceZ(counts);
  const phz = physZ(counts);
  return (
    <tr className="border-t border-[var(--color-border)]">
      <td className="py-1">
        <Link
          to={`/wallet/${familyID}`}
          className="num underline decoration-dotted underline-offset-2"
          title={familyID}
        >
          {shortWallet(familyID)}
        </Link>
      </td>
      <td className="py-1 text-right num">{counts.signals}</td>
      <td className="py-1 text-right num">{counts.settled}</td>
      <td className="py-1 text-right num">{hitWord(counts.won, counts.settled)}</td>
      <td
        className="py-1 text-right num"
        style={{ color: zTone(pz) }}
        title={
          counts.takerPriced === 0
            ? "стакана на этих строках не наблюдали: сравнивать не с чем"
            : `кромка ${pct(priceEdge(counts))} по ${counts.takerPriced} строкам`
        }
      >
        {zWord(pz)}
      </td>
      <td
        className="py-1 text-right num"
        style={{ color: zTone(phz) }}
        title={
          counts.physPriced === 0
            ? "замка на этих строках не наблюдали: строка в сравнение с физикой не входит"
            : `кромка ${pct(physEdge(counts))} по ${counts.physPriced} строкам`
        }
      >
        {zWord(phz)}
      </td>
      {/* СУММЫ за окно, а не удельная ставка — против правила соседних панелей,
          и намеренно: там строки — группы одной подсистемы с общим сроком
          жизни, здесь сроки строк-семей расходятся в десятки раз. Семья,
          прожившая в журнале час, превращала −$50 на двух заявках в
          «−$1031/сут», и колонка ставок складывалась глазом в общий минус
          портфеля, которого нет. Ставка — вместе со сроком и плотностью, без
          которых её не прочесть, — в подписи под таблицей. Мейкер и тейкер
          порознь: это разные гипотезы с разными знаменателями, и «один счёт»
          слил бы их молча. */}
      <td
        className="num py-1 text-right"
        style={{ color: moneyTone(raw.shadow_maker_pnl) }}
        title={`мейкерская тень по ${raw.maker_filled} налитым; ставка и срок — в подписи под таблицей`}
      >
        {money(raw.shadow_maker_pnl)}
      </td>
      <td
        className="num py-1 text-right"
        style={{ color: moneyTone(raw.shadow_taker_pnl) }}
        title={`тейкерская тень по ${counts.settled} сведённым`}
      >
        {money(raw.shadow_taker_pnl)}
      </td>
      <td className="py-1 text-right">
        <PnLShape shape={shape} />
      </td>
    </tr>
  );
}

/**
 * Срез по ревизии состава пула.
 *
 * # Зачем кнопки, а если ревизия одна — ничего
 *
 * Сводка за окно складывает строки СТОЛЬКИХ пулов, сколько раз за это окно
 * пересобрался состав. Складывать их можно — состав неизменяем внутри ревизии,
 * и строки честны каждая по себе, — но одно число из четырёх отборов читается
 * как одна выборка, и именно так его и прочитали: минус суток целиком сделала
 * одна семья под ревизиями 8 и 10, а панель показывала общий минус.
 *
 * Поэтому смесь названа словами ВСЕГДА, а срез предложен кнопкой. Умолчание —
 * «все»: резать молча по последней ревизии значило бы менять знаменатель
 * сводки самостоятельно, при каждой пересборке пула.
 *
 * Когда ревизия в окне одна, узла нет вовсе: выбирать не из чего, а
 * предупреждение, которое всегда на месте, глаз перестаёт читать.
 */
function RevCut({
  rev,
  revisions,
  onPick,
}: {
  rev: number;
  revisions: FamilyRevRows[];
  onPick: (rev: number) => void;
}) {
  if (revisions.length < 2) return null;
  return (
    <div className="mt-3 text-xs text-[var(--color-muted)]">
      <span>{revMixWord(revisions)}. Считается: </span>
      <b>{revCutWord(rev)}</b>
      <span className="ml-2 inline-flex flex-wrap gap-1">
        <RevButton label="все" active={rev === 0} onPick={() => onPick(0)} />
        {revisions.map((r) => (
          <RevButton
            key={r.rev}
            label={`${r.rev}`}
            active={rev === r.rev}
            onPick={() => onPick(r.rev)}
          />
        ))}
      </span>
    </div>
  );
}

function RevButton({
  label,
  active,
  onPick,
}: {
  label: string;
  active: boolean;
  onPick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onPick}
      aria-pressed={active}
      className={`rounded border px-1.5 py-0.5 ${
        active
          ? "border-[var(--color-fg)] text-[var(--color-fg)]"
          : "border-[var(--color-border)] hover:text-[var(--color-fg)]"
      }`}
    >
      {label}
    </button>
  );
}

/**
 * Разрез по числу сошедшихся семей — «одна» против «две и больше».
 *
 * Не украшение: замер вне выборки дал 0.742 при 56 срабатываниях в сутки для
 * консенсуса ОДНОЙ семьи и 0.840 при 8 в сутки для консенсуса ДВУХ — но у
 * вторых кромка над физикой ровно нулевая (0.840 против 0.840), то есть
 * срабатывание позднее и не сообщает ничего сверх уже известного. Слить два
 * архетипа в одно число значило бы спрятать это различие.
 */
function AgreementNote({
  signals,
  overall,
}: {
  signals: readonly FamilySignal[];
  overall: EdgeCounts;
}) {
  const split = splitByAgreement(signals);
  const zachetnye = signals.filter((s) => !s.rule).length;
  // Лента отдаёт не больше `limit` строк: если зачётных в ней меньше, чем
  // сервер насчитал за весь период, разрез недосчитал хвост, и это обязано
  // быть видно, а не спрятано в округлении.
  const truncated = zachetnye < overall.signals;
  return (
    <ul className="mt-3 space-y-1 text-xs text-[var(--color-muted)]">
      {(["one", "many"] as const).map((g) => (
        <li key={g}>
          <b>{agreedLabel(g)}</b> — {verdict(split[g])}.
        </li>
      ))}
      {truncated && (
        <li className="text-[var(--color-warn)]">
          разрез посчитан по {zachetnye} из {overall.signals} зачётных строк за
          окно: лента отдаёт не больше 500 строк за раз, и на широком окне хвост
          в него не поместился.
        </li>
      )}
    </ul>
  );
}

/**
 * Состав пула на одну ревизию: одна строка на семью, раскрывающаяся до состава
 * и улик связи.
 *
 * Отвергнутые семьи показаны НАРАВНЕ с пущенными: без них нельзя ответить,
 * почему пул именно такой, а не шире или уже.
 */
export function FamilyRoster() {
  const pool = useFamilyPool();
  const [open, setOpen] = useState<ReadonlySet<string>>(new Set());
  const toggle = useCallback((id: string) => {
    setOpen((prev) => {
      const next = new Set(prev);
      if (!next.delete(id)) next.add(id);
      return next;
    });
  }, []);

  const roster = pool.data?.roster;
  const links = pool.data?.links ?? [];

  return (
    <section>
      <h3 className="mb-2 text-xs font-medium text-[var(--color-muted)]">
        состав пула{roster && roster.rev > 0 ? ` — ревизия ${roster.rev}` : ""}
      </h3>
      {!pool.data ? (
        <Empty>{pool.isError ? "состав не загрузился" : "загрузка…"}</Empty>
      ) : !roster || roster.families.length === 0 ? (
        <Empty>пул ещё не собран: улик связи пока не набралось</Empty>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-left text-[var(--color-muted)]">
              <tr>
                <th className="w-6 pb-2" />
                <th className="pb-2 font-normal">семья</th>
                <th className="pb-2 text-right font-normal">кошельков</th>
                <th className="pb-2 text-right font-normal">плотность</th>
                <th className="pb-2 text-right font-normal">окон</th>
                <th className="pb-2 text-right font-normal">остаток</th>
                <th className="pb-2 font-normal">статус</th>
              </tr>
            </thead>
            <tbody>
              {roster.families.map((f) => (
                <Fragment key={f.family_id}>
                  <FamilyPoolRow
                    family={f}
                    expanded={open.has(f.family_id)}
                    onToggle={() => toggle(f.family_id)}
                  />
                  {open.has(f.family_id) && (
                    <FamilyPoolDetailRow
                      family={f}
                      links={linksWithin(
                        f.members.map((m) => m.address),
                        links,
                      )}
                      colSpan={7}
                    />
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function FamilyPoolRow({
  family,
  expanded,
  onToggle,
}: {
  family: Family;
  expanded: boolean;
  onToggle: () => void;
}) {
  const z = residZ(family);
  return (
    <tr className="border-t border-[var(--color-border)]">
      <td className="py-1">
        <button
          type="button"
          onClick={onToggle}
          className="text-[var(--color-muted)] hover:text-[var(--color-fg)]"
          aria-expanded={expanded}
          title="состав и улики связи"
        >
          {expanded ? "▾" : "▸"}
        </button>
      </td>
      <td className="py-1">
        <Link
          to={`/wallet/${family.family_id}`}
          className="num underline decoration-dotted underline-offset-2"
          title={family.family_id}
        >
          {shortWallet(family.family_id)}
        </Link>
      </td>
      <td className="py-1 text-right num">{walletsWord(family)}</td>
      <td className="py-1 text-right num">{share(family.density)}</td>
      <td className="py-1 text-right num">{family.windows}</td>
      <td
        className="py-1 text-right num"
        style={{ color: zTone(z) }}
        title="остаток над физикой, делённый на sqrt(Σp(1−p)) — порог входа в пул"
      >
        {zWord(z)}
      </td>
      <td className="py-1" style={{ color: poolTone(family) }}>
        {poolWord(family)}
      </td>
    </tr>
  );
}

function FamilyPoolDetailRow({
  family,
  links,
  colSpan,
}: {
  family: Family;
  links: FamilyLink[];
  colSpan: number;
}) {
  return (
    <tr className="border-t border-[var(--color-border)]">
      <td colSpan={colSpan} className="bg-[var(--color-bg)] px-3 py-2">
        <div className="mb-2">
          <span className="text-[var(--color-muted)]">состав:</span>
          <ul className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5">
            {family.members.map((m) => (
              <li key={m.address}>
                <Link
                  to={`/wallet/${m.address}`}
                  className="num underline decoration-dotted underline-offset-2"
                >
                  {shortWallet(m.address)}
                </Link>{" "}
                <span className="text-[var(--color-muted)]">
                  с {new Date(m.since).toLocaleDateString("ru-RU")}
                </span>
              </li>
            ))}
          </ul>
        </div>
        {links.length === 0 ? (
          <p className="text-[var(--color-muted)]">
            улик связи внутри этого состава не нашлось
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[11px]">
              <thead className="text-left text-[var(--color-muted)]">
                <tr>
                  <th className="pb-1 font-normal">пара</th>
                  <th className="pb-1 font-normal">улика</th>
                  <th className="pb-1 text-right font-normal">общих окон</th>
                  <th className="pb-1 text-right font-normal">совпало</th>
                  <th className="pb-1 text-right font-normal">ждали бы</th>
                  <th className="pb-1 text-right font-normal">согласие</th>
                  <th className="pb-1 text-right font-normal">размер</th>
                </tr>
              </thead>
              <tbody>
                {links.map((l) => (
                  <tr
                    key={`${l.addr_a}|${l.addr_b}|${l.source}`}
                    className="border-t border-[var(--color-border)]"
                  >
                    <td className="py-1">
                      <span className="num">{shortWallet(l.addr_a)}</span>
                      {" ↔ "}
                      <span className="num">{shortWallet(l.addr_b)}</span>
                    </td>
                    <td className="py-1">{linkSourceWord(l.source)}</td>
                    <td className="py-1 text-right num">{l.shared}</td>
                    <td className="py-1 text-right num">{l.hits}</td>
                    <td
                      className="py-1 text-right num"
                      title="сколько совпадений дал бы нуль: моменты одного из пары переставлены между их же общими окнами"
                    >
                      {expectedWord(l.expected)}
                    </td>
                    <td className="py-1 text-right num">{share(l.agree)}</td>
                    <td className="py-1 text-right num">{share(l.size_ratio)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </td>
    </tr>
  );
}

/**
 * Лента сигналов консенсуса — СВЁРНУТАЯ, как и соседние ленты.
 *
 * Отдельным компонентом, чтобы под FoldablePanel тело не монтировалось, пока
 * карточку не открыли: свёрнутая лента не заводит опроса раз в пятнадцать
 * секунд, а страница отвечает на вопрос «есть ли кромка», а не «что
 * происходило».
 */
export function FamilyFeed({ hours, windowLen }: { hours: number; windowLen: number }) {
  return (
    <FoldablePanel
      title="Сигналы консенсуса семьи"
      subtitle={`за ${hours} ч, по строке на окно × сторону`}
    >
      <FamilyFeedBody hours={hours} windowLen={windowLen} />
    </FoldablePanel>
  );
}

function FamilyFeedBody({ hours, windowLen }: { hours: number; windowLen: number }) {
  const feed = useFamilySignals(hours, undefined, undefined, windowLen);
  const signals = feed.data?.signals ?? [];

  return (
    <>
      {!feed.data ? (
        <Empty>{feed.isError ? "лента не загрузилась" : "загрузка…"}</Empty>
      ) : signals.length === 0 ? (
        <Empty>строк за период не было</Empty>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-[var(--color-panel)]">
              <tr className="text-left text-[var(--color-muted)]">
                <th className="pb-2 font-normal">когда</th>
                <th className="pb-2 font-normal">рынок</th>
                <th className="pb-2 font-normal">исход</th>
                <th className="pb-2 font-normal">семья</th>
                <th className="pb-2 text-right font-normal">сошлось семей</th>
                <th className="pb-2 text-right font-normal">кошельков</th>
                <th className="pb-2 text-right font-normal">оборот</th>
                <th className="pb-2 text-right font-normal">фора</th>
                <th className="pb-2 text-right font-normal">мейкер</th>
                <th className="pb-2 text-right font-normal">тейкер</th>
                <th className="pb-2 text-right font-normal">физика</th>
                <th className="pb-2 font-normal">что стало</th>
                {/* Пустой заголовок: колонка появляется только на ступени alert
                    и выше, и подписывать её словом «действие» значило бы
                    обещать кнопку там, где её не будет. */}
                <th className="pb-2 font-normal"></th>
              </tr>
            </thead>
            <tbody>
              {signals.map((s) => (
                <FamilyFeedRow key={s.id} signal={s} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function FamilyFeedRow({ signal }: { signal: FamilySignal }) {
  return (
    <tr className="border-t border-[var(--color-border)]">
      <td className="py-1 text-[var(--color-muted)]">
        {new Date(signal.detected_at).toLocaleTimeString("ru-RU")}
      </td>
      <td className="py-1">{marketWord(signal)}</td>
      <td className="py-1">
        {signal.outcome_name || signal.asset_id.slice(0, 8)}
        {signal.reversal ? (
          <span
            className="ml-1 text-[var(--color-muted)]"
            title="вторая строка окна: семья развернулась и набрала столько же в противоположную сторону"
          >
            ↺
          </span>
        ) : null}
      </td>
      <td className="py-1">
        <Link
          to={`/wallet/${signal.family_id}`}
          className="num underline decoration-dotted underline-offset-2"
          title={signal.family_id}
        >
          {shortWallet(signal.family_id)}
        </Link>
      </td>
      <td
        className="py-1 text-right num"
        title={agreedLabel(agreedGroup(signal.families_agreed))}
      >
        {signal.families_agreed}
      </td>
      <td
        className="py-1 text-right num"
        title={`${signal.wallets_agreed} сошлось из ${signal.wallets_seen} задели окно`}
      >
        {signal.wallets_agreed}
      </td>
      <td className="py-1 text-right num">{usd(signal.family_usd)}</td>
      <td className="py-1 text-right num">{lead(signal.lead_ms)}</td>
      <td className="py-1 text-right num">
        {Number(signal.plan_price) > 0 ? signal.plan_price : "—"}
        {signal.maker_filled === true ? " ✓" : signal.maker_filled === false ? " ✗" : ""}
      </td>
      <td className="py-1 text-right num">{signal.taker_price ?? "—"}</td>
      <td
        className="py-1 text-right num text-[var(--color-muted)]"
        title={
          signal.p_phys
            ? `вероятность нашей стороны по замку, ревизия ${signal.model_rev ?? "?"}`
            : "замка на этот момент не наблюдали: строка в сравнение с физикой не входит"
        }
      >
        {signal.p_phys ?? "—"}
      </td>
      <td className="py-1" style={{ color: rowTone(signal) }}>
        {outcomeWord(signal)}
      </td>
      <td className="py-1">
        <StrategyRowActions
          strategy="family"
          signalID={signal.id}
          windowEnd={signal.window_end}
        />
      </td>
    </tr>
  );
}
